import { useState } from 'react'
import {
  GitBranch,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Check,
  Copy,
  RefreshCw,
  CheckCircle2,
  Circle,
} from 'lucide-react'
import type { ToolKey, ToolState, ActionItem } from '@/types/radar'
import type { ImplementationThread } from '@/lib/radar/threads'
import type { ThreadProgress } from '@/lib/radar/thread-progress'
import { EFFORT_LABELS } from '@/lib/radar/scoring'
import {
  buildActionPrompt,
  buildSovSteps,
  deriveStepVerifyKeys,
} from '@/lib/radar/prompt-builder'

// ── Thread Step Prompt Builder ─────────────────────────────────────────────────

export function buildThreadStepPrompt(
  thread: ImplementationThread,
  step: ImplementationThread['steps'][number],
  stepIndex: number,
  actions: ActionItem[],
  tools: Record<ToolKey, ToolState>,
  domain: string,
  brand: string,
  entityAssertable = false
): string {
  const stepActions = actions.filter(a => step.actionIds.includes(a.id))
  const previousSteps = thread.steps.filter(s => s.order < step.order)

  const sections: string[] = [
    `You are helping implement AI visibility fixes for ${domain} (brand: ${brand}).`,
    ...(entityAssertable
      ? []
      : [
          `NOTE: the brand name above was NOT verified against ${domain}. Do not write it into schema markup, llms.txt, or any published metadata — confirm the exact registered name with the site owner first.`,
        ]),
    `This is Step ${stepIndex + 1} of ${thread.steps.length} in the "${thread.name}" implementation thread.`,
    '',
    '## Thread Context',
    '',
    thread.description,
  ]

  if (previousSteps.length > 0) {
    sections.push(
      '',
      '## Previous Steps (already completed)',
      '',
      ...previousSteps.map(s => `${s.order}. ${s.title}: ${s.description}`)
    )
  }

  sections.push('', `## Current Step: ${step.title}`, '', step.description)

  if (stepActions.length > 0) {
    sections.push('', '## Specific Actions Required')

    // A step can bundle multiple SOV actions (e.g. "Outrank ..." and
    // "Expand SOV to N more...") and, in a mixed-source step, non-SOV
    // actions alongside them. All SOV actions in the step share the same
    // underlying SOV state, competitors, and provider gap, so that context
    // is rendered once here instead of once per action -- each SOV action
    // then contributes only its own title/why/how, never a re-run of the
    // shared template. Non-SOV actions render through the existing
    // buildActionPrompt path regardless of whether the step also has SOV
    // actions.
    const hasSovAction = stepActions.some(a => a.category === 'sov')

    if (hasSovAction) {
      const sharedSteps = buildSovSteps(tools)
      if (sharedSteps) {
        sections.push('', '### Shared SOV Context', '', sharedSteps)
      }
    }

    for (const action of stepActions) {
      if (action.category === 'sov') {
        sections.push(
          '',
          `### ${action.title}`,
          '',
          `Why: ${action.why}`,
          '',
          `How: ${action.how}`
        )
        continue
      }

      const prompt = buildActionPrompt(
        action,
        tools,
        domain,
        brand,
        entityAssertable
      )
      const stepsSection = prompt
        .split('## Implementation Steps')[1]
        ?.split('## How to Verify')[0]
      sections.push(
        '',
        `### ${action.title}`,
        '',
        `Why: ${action.why}`,
        '',
        stepsSection ? `Steps:${stepsSection.trim()}` : action.how
      )
    }
  }

  const stepVerifyKeys = deriveStepVerifyKeys(
    step.actionIds,
    actions,
    tools,
    thread.verifyToolKeys
  )
  sections.push(
    '',
    '## Verification',
    '',
    `After completing this step, re-run the following Radar tool(s) to verify: ${stepVerifyKeys.join(', ')}`,
    '',
    '---',
    `Source: Radar AI Visibility Audit | Thread: ${thread.name} | Step ${stepIndex + 1}/${thread.steps.length}`
  )

  return sections.join('\n')
}

// ── Step Card ──────────────────────────────────────────────────────────────────

function StepCard({
  thread,
  step,
  stepIndex,
  actions,
  tools,
  domain,
  brand,
  entityAssertable = false,
  onRerun,
  progress,
  onToggleComplete,
}: {
  thread: ImplementationThread
  step: ImplementationThread['steps'][number]
  stepIndex: number
  actions: ActionItem[]
  tools: Record<ToolKey, ToolState>
  domain: string
  brand: string
  /** Whether the resolved entity may be written into published output. */
  entityAssertable?: boolean
  onRerun?: (key: ToolKey) => void
  progress?: ThreadProgress
  onToggleComplete?: (threadId: string, stepOrder: number) => void
}) {
  const [showPrompt, setShowPrompt] = useState(false)
  const [copied, setCopied] = useState(false)
  const [promptText, setPromptText] = useState('')

  const stepProgress = progress?.steps[String(step.order)]
  const isComplete = stepProgress?.completed || false
  const stepVerifyKeys = deriveStepVerifyKeys(
    step.actionIds,
    actions,
    tools,
    thread.verifyToolKeys
  )

  function handleGeneratePrompt() {
    if (showPrompt) {
      setShowPrompt(false)
      return
    }
    const prompt = buildThreadStepPrompt(
      thread,
      step,
      stepIndex,
      actions,
      tools,
      domain,
      brand,
      entityAssertable
    )
    setPromptText(prompt)
    setShowPrompt(true)
  }

  function handleCopy() {
    navigator.clipboard.writeText(promptText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div
      className='rounded-lg border p-3'
      style={{
        borderColor: isComplete ? '#22c55e30' : 'var(--border)',
        background: isComplete ? '#22c55e08' : 'var(--background)',
      }}
    >
      <div className='flex items-start gap-3'>
        <button
          onClick={() => onToggleComplete?.(thread.id, step.order)}
          className='mt-0.5 flex-shrink-0 transition-colors'
          style={{ color: isComplete ? '#22c55e' : 'var(--muted-foreground)' }}
        >
          {isComplete ? <CheckCircle2 size={14} /> : <Circle size={14} />}
        </button>
        <div className='flex-1 min-w-0'>
          <span
            className='block text-xs font-semibold mb-1'
            style={{
              color: isComplete ? '#22c55e' : 'var(--foreground)',
              textDecoration: isComplete ? 'line-through' : 'none',
              opacity: isComplete ? 0.7 : 1,
            }}
          >
            Step {step.order}: {step.title}
          </span>
          <span className='block text-[11px] leading-relaxed mb-2 text-muted-foreground'>
            {step.description}
          </span>

          {stepProgress?.verifiedScore !== undefined && (
            <span
              className='block text-[11px] mb-2'
              style={{ color: '#22c55e' }}
            >
              Live score: {stepProgress.verifiedScore}/100
            </span>
          )}

          <div className='flex items-center gap-2 flex-wrap'>
            <button
              onClick={handleGeneratePrompt}
              className='flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border transition-all hover:brightness-125'
              style={{
                borderColor: showPrompt ? `${thread.color}40` : 'var(--border)',
                background: showPrompt ? `${thread.color}15` : 'transparent',
                color: thread.color,
              }}
            >
              <Sparkles size={9} />
              {showPrompt ? 'Hide' : 'AI Prompt'}
            </button>

            {onRerun && (
              <button
                onClick={() => stepVerifyKeys.forEach(k => onRerun(k))}
                className='flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-all hover:brightness-125'
                style={{
                  borderColor: 'var(--border)',
                  color: 'var(--muted-foreground)',
                }}
              >
                <RefreshCw size={9} /> Verify
              </button>
            )}

            <span className='text-[11px] text-muted-foreground'>
              {step.actionIds.length} action
              {step.actionIds.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {showPrompt && promptText && (
        <div
          className='mt-3 rounded-lg border overflow-hidden'
          style={{
            borderColor: `${thread.color}30`,
            background: 'var(--card)',
          }}
        >
          <div className='flex items-center justify-between px-3 py-2 border-b border-border'>
            <span
              className='text-[11px] font-semibold'
              style={{ color: thread.color }}
            >
              Thread prompt - step {step.order}
            </span>
            <button
              onClick={handleCopy}
              className='flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-all hover:brightness-125'
              style={{
                borderColor: copied ? '#22c55e40' : 'var(--border)',
                background: copied ? '#22c55e10' : 'transparent',
                color: copied ? '#22c55e' : 'var(--muted-foreground)',
              }}
            >
              {copied ? (
                <>
                  <Check size={9} /> Copied
                </>
              ) : (
                <>
                  <Copy size={9} /> Copy
                </>
              )}
            </button>
          </div>
          <textarea
            readOnly
            value={promptText}
            className='w-full text-[11px] leading-relaxed p-3 resize-none focus:outline-none'
            style={{
              background: 'transparent',
              color: 'var(--muted-foreground)',
              minHeight: '200px',
              maxHeight: '400px',
            }}
            rows={Math.min(20, promptText.split('\n').length + 2)}
          />
        </div>
      )}
    </div>
  )
}

// ── Thread Card ────────────────────────────────────────────────────────────────

function ThreadCard({
  thread,
  actions,
  tools,
  domain,
  brand,
  entityAssertable = false,
  onRerun,
  progress,
  onToggleComplete,
}: {
  thread: ImplementationThread
  actions: ActionItem[]
  tools: Record<ToolKey, ToolState>
  domain: string
  brand: string
  /** Whether the resolved entity may be written into published output. */
  entityAssertable?: boolean
  onRerun?: (key: ToolKey) => void
  progress?: ThreadProgress
  onToggleComplete?: (threadId: string, stepOrder: number) => void
}) {
  const [expanded, setExpanded] = useState(false)

  const completedSteps = thread.steps.filter(
    s => progress?.steps[String(s.order)]?.completed
  ).length
  const progressPct =
    thread.steps.length > 0
      ? Math.round((completedSteps / thread.steps.length) * 100)
      : 0

  return (
    <div className='rounded-xl border overflow-hidden transition-all border-border bg-card'>
      <button
        onClick={() => setExpanded(!expanded)}
        className='w-full flex items-start gap-3 p-4 text-left'
      >
        <div
          className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
          style={{ background: `${thread.color}15` }}
        >
          <GitBranch size={14} style={{ color: thread.color }} />
        </div>

        <div className='flex-1 min-w-0'>
          <div className='flex items-baseline gap-2 mb-1 flex-wrap'>
            <span className='text-xs font-semibold text-foreground leading-none'>
              {thread.name}
            </span>
            <span
              className='text-[11px] font-semibold px-1.5 py-0.5 rounded leading-none'
              style={{
                background: `${EFFORT_LABELS[thread.estimatedEffort].color}10`,
                color: EFFORT_LABELS[thread.estimatedEffort].color,
              }}
            >
              {EFFORT_LABELS[thread.estimatedEffort].label}
            </span>
            <span
              className='text-[11px] px-1.5 py-0.5 rounded font-mono'
              style={{ background: `${thread.color}15`, color: thread.color }}
            >
              {thread.steps.length} step{thread.steps.length !== 1 ? 's' : ''} /{' '}
              {thread.actionCount} action{thread.actionCount !== 1 ? 's' : ''}
            </span>
          </div>
          <span className='block text-[11px] leading-relaxed text-muted-foreground'>
            {thread.description}
          </span>

          {/* Progress bar */}
          {progress && (
            <div className='mt-2 flex items-center gap-2'>
              <div className='flex-1 h-1.5 rounded-full overflow-hidden bg-muted'>
                <div
                  className='h-full rounded-full transition-all'
                  style={{
                    width: `${progressPct}%`,
                    background: progressPct === 100 ? '#22c55e' : thread.color,
                  }}
                />
              </div>
              <span className='text-[11px] font-mono text-muted-foreground'>
                {completedSteps}/{thread.steps.length}
              </span>
            </div>
          )}
        </div>

        {expanded ? (
          <ChevronUp size={14} className='text-muted-foreground' />
        ) : (
          <ChevronDown size={14} className='text-muted-foreground' />
        )}
      </button>

      {expanded && (
        <div className='px-4 pb-4 pt-0 space-y-2.5 border-t border-border'>
          <div className='pt-3'>
            <span
              className='block text-[11px] font-bold mb-2'
              style={{ color: thread.color }}
            >
              Implementation Steps
            </span>
          </div>
          {thread.steps.map((step, i) => (
            <StepCard
              key={step.order}
              thread={thread}
              step={step}
              stepIndex={i}
              actions={actions}
              tools={tools}
              domain={domain}
              brand={brand}
              entityAssertable={entityAssertable}
              onRerun={onRerun}
              progress={progress}
              onToggleComplete={onToggleComplete}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main Panel ─────────────────────────────────────────────────────────────────

export function ThreadsPanel({
  threads,
  actions,
  tools,
  domain,
  brand,
  entityAssertable = false,
  onRerun,
  progressMap,
  onToggleComplete,
}: {
  threads: ImplementationThread[]
  actions: ActionItem[]
  tools: Record<ToolKey, ToolState>
  domain: string
  brand: string
  /** Whether the resolved entity may be written into published output. */
  entityAssertable?: boolean
  onRerun?: (key: ToolKey) => void
  progressMap?: Record<string, ThreadProgress>
  onToggleComplete?: (threadId: string, stepOrder: number) => void
}) {
  return (
    <div className='space-y-4'>
      <div className='flex items-baseline gap-2'>
        <GitBranch
          size={15}
          style={{ color: '#008dcc' }}
          className='relative top-[2px]'
        />
        <h2 className='text-sm font-semibold text-foreground leading-none'>
          Implementation Threads
        </h2>
        <span
          className='text-[11px] px-1.5 py-0.5 rounded font-mono leading-none'
          style={{ background: '#008dcc15', color: '#008dcc' }}
        >
          {threads.length}
        </span>
      </div>

      <span className='block text-[11px] leading-relaxed text-muted-foreground'>
        Structured implementation paths based on your audit results. Each thread
        groups related fixes into ordered steps with AI-ready prompts you can
        paste into Claude, ChatGPT, or Cursor.
      </span>

      <div className='space-y-2.5'>
        {threads.map(thread => (
          <ThreadCard
            key={thread.id}
            thread={thread}
            actions={actions}
            tools={tools}
            domain={domain}
            brand={brand}
            entityAssertable={entityAssertable}
            onRerun={onRerun}
            progress={progressMap?.[thread.id]}
            onToggleComplete={onToggleComplete}
          />
        ))}
        {threads.length === 0 && (
          <span className='block text-xs text-center py-8 text-muted-foreground'>
            No implementation threads generated. Run an audit to see actionable
            threads.
          </span>
        )}
      </div>
    </div>
  )
}
